/**
 * The marble: a scalar `s` along the centerline plus a rolling visual.
 *
 * The kit's marble mesh is grounded like every other piece (origin = bottom
 * centre), and the centerline runs along the channel FLOOR — so the outer
 * object sits exactly on the curve point and an inner pivot lifts the mesh by
 * its radius. Roll is applied to the pivot, which keeps the contact point put.
 */
import * as THREE from "three";
import type { Assets } from "./assets.ts";
import type { FanField } from "./fanField.ts";
import { addMarbleCore, disposeMarbleOwnedVisuals } from "./marbleGlass.ts";

const MARBLE_PIECE = "marble-center-high";
const BASE_SPEED = 2.2;
const MIN_SPEED = 1.2;
const MAX_SPEED = 7;
const GRAVITY = 6;
const DRAG = 0.9;

export interface Marble {
  obj: THREE.Object3D;
  /** Distance travelled along the curve (wraps at curve length). */
  s: number;
  speed: number;
  radius: number;
  /** True only while a trackside boost is actively accelerating it. */
  boosting: boolean;
  update(dt: number): void;
  dispose(): void;
}

export function createMarble(
  assets: Assets,
  curve: THREE.Curve<THREE.Vector3>,
  /** Alternate-lap variant (a diamond's other branch); laps alternate. */
  altCurve?: THREE.Curve<THREE.Vector3>,
  /** Trackside fan boosts: accelerate while the marble is within range. */
  boosts: { pos: [number, number, number]; strength: number }[] = [],
  /** Bridge holes: the marble may drop through onto the channel below —
   * deterministic per lap, so replays of a seed are identical. */
  fallPoints: {
    pos: [number, number, number];
    landPos: [number, number, number];
    /** Takeable spur drains always drop (end-hole law). */
    guaranteed?: boolean;
  }[] = [],
  /** M19a loop backport: the endless wind-field law over the loop's own
   * fans. The kinematic marble is curve-locked, so wind resolves into
   * its two honest effects: the tangential component works the speed,
   * the lateral component sways the marble inside the channel (a
   * spring-damped visual offset, clamped well inside the walls). */
  windField?: FanField,
): Marble {
  const obj = new THREE.Group();
  obj.name = "marble";

  const pivot = new THREE.Group();
  obj.add(pivot);

  const mesh = assets.getPiece(MARBLE_PIECE);
  let radius = 0.1;
  if (mesh) {
    // Half the kit's authored size (user: "shrink the ball 50%").
    mesh.scale.setScalar(0.5);
    // The kit shell is empty glass — inject the opaque core so the
    // marble reads solid (see marbleGlass.marbleCoreMesh).
    addMarbleCore(mesh);
    // Recenter the grounded mesh inside the pivot, then lift the pivot by the
    // same offset so the sphere's contact point stays at the object's origin.
    const box = new THREE.Box3().setFromObject(mesh);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    radius = Math.max(size.x, size.y, size.z) / 2 || 0.2;
    pivot.position.copy(center);
    mesh.position.sub(center);
    pivot.add(mesh);
  } else {
    console.warn(
      `[marble] missing piece "${MARBLE_PIECE}" — using a fallback sphere`,
    );
    const fallback = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 24, 16),
      new THREE.MeshStandardMaterial({
        color: 0x4fc3f7,
        roughness: 0.15,
        metalness: 0.1,
      }),
    );
    fallback.userData.marbleOwnedVisual = true;
    fallback.castShadow = true;
    pivot.position.y = radius;
    pivot.add(fallback);
  }

  // Laps alternate between the two curve variants (diamond branches).
  const variants = altCurve ? [curve, altCurve] : [curve];
  let lap = 0;
  const active = () => variants[lap % variants.length];

  // Map each bridge hole to arc positions on every variant: the hole s
  // (where the drop can trigger) and the landing s on the channel below.
  // Dense uniform sampling is fine — both points are ≥0.5 apart in y from
  // any other part of the loop passing that cell.
  const closestS = (
    c: THREE.Curve<THREE.Vector3>,
    p: [number, number, number],
  ): { s: number; dist: number } => {
    const total = c.getLength();
    const probe = new THREE.Vector3();
    let best = 0;
    let bestD = Infinity;
    const steps = 600;
    for (let i = 0; i < steps; i++) {
      c.getPointAt(i / steps, probe);
      const d = probe.distanceToSquared(new THREE.Vector3(...p));
      if (d < bestD) {
        bestD = d;
        best = (i / steps) * total;
      }
    }
    return { s: best, dist: Math.sqrt(bestD) };
  };
  const falls = variants.map((c) =>
    fallPoints.map((f) => {
      const hole = closestS(c, f.pos);
      const land = closestS(c, f.landPos);
      return {
        holeS: hole.s,
        landS: land.s,
        dropY: f.pos[1] - f.landPos[1],
        // A hole is live only on variants that actually PASS it — a
        // takeable spur's hole lies on the branch variant alone; mapping
        // it onto the main line would fire a phantom drop at the nearest
        // unrelated point.
        active: hole.dist < 0.5,
        guaranteed: f.guaranteed === true,
      };
    })
  );
  // Falling state: eased vertical drop from the hole to the landing.
  let falling:
    | { t: number; from: THREE.Vector3; toS: number; drop: number }
    | null = null;
  const FALL_TIME = 0.35;
  /** Deterministic per (lap, hole): drops on roughly half the passes. */
  const takesFall = (lapN: number, holeI: number): boolean =>
    ((lapN * 2654435761 + holeI * 40503 + 12345) >>> 0) % 100 < 50;

  const up = new THREE.Vector3(0, 1, 0);
  const point = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  const axis = new THREE.Vector3();
  const spin = new THREE.Quaternion();
  const lateral = new THREE.Vector3();

  // Wind sway state: lateral offset inside the channel, spring-damped.
  // ±0.15 keeps the (0.2-radius) marble clear of the 0.5-half-width walls.
  const SWAY_MAX = 0.15;
  const SWAY_K = 18; // spring toward the centerline
  const SWAY_D = 6; // damping
  let sway = 0;
  let swayV = 0;
  const windAccel: [number, number, number] = [0, 0, 0];

  const marble: Marble = {
    obj,
    s: 0,
    speed: BASE_SPEED,
    radius,
    boosting: false,
    dispose(): void {
      disposeMarbleOwnedVisuals(obj);
    },
    update(dt: number) {
      const total = active().getLength();
      if (total <= 0 || dt <= 0) return;
      // Boost is an acceleration state, not a lingering appearance. Clear it
      // before any early-returning fall path so a bridge drop cannot draw a
      // hot vertical streak after the marble has left the fan.
      marble.boosting = false;

      // Mid-fall: eased vertical drop, then resume on the lower channel.
      if (falling) {
        falling.t += dt / FALL_TIME;
        if (falling.t >= 1) {
          marble.s = falling.toS;
          falling = null;
        } else {
          const k = falling.t * falling.t; // accelerate downward
          obj.position.set(
            falling.from.x,
            falling.from.y - k * falling.drop,
            falling.from.z,
          );
          return;
        }
      }

      const u = ((marble.s % total) + total) % total / total;
      active().getTangentAt(u, tangent);

      // Gravity-flavoured: downhill accelerates, uphill and drag bleed speed.
      marble.speed +=
        (-tangent.y * GRAVITY - DRAG * (marble.speed - BASE_SPEED)) * dt;
      // Trackside fans shove the marble along while it's in front of them.
      for (const b of boosts) {
        const dx = obj.position.x - b.pos[0];
        const dz = obj.position.z - b.pos[2];
        if (dx * dx + dz * dz < 1.2 * 1.2) {
          marble.speed += b.strength * dt;
          marble.boosting = true;
        }
      }
      // Wind (M19a): tangential component works the speed; lateral
      // component drives the in-channel sway spring.
      let latAccel = 0;
      if (windField) {
        windAccel[0] = 0;
        windAccel[1] = 0;
        windAccel[2] = 0;
        if (
          windField.forceAt(
            [obj.position.x, obj.position.y, obj.position.z],
            1,
            windAccel,
          )
        ) {
          marble.speed += (windAccel[0] * tangent.x +
            windAccel[1] * tangent.y +
            windAccel[2] * tangent.z) * dt;
          lateral.copy(up).cross(tangent);
          if (lateral.lengthSq() > 1e-8) {
            lateral.normalize();
            latAccel = windAccel[0] * lateral.x + windAccel[2] * lateral.z;
          }
        }
      }
      swayV += (latAccel - SWAY_K * sway - SWAY_D * swayV) * dt;
      sway = Math.max(-SWAY_MAX, Math.min(SWAY_MAX, sway + swayV * dt));
      marble.speed = Math.min(MAX_SPEED, Math.max(MIN_SPEED, marble.speed));

      const prevS = marble.s;
      const next = marble.s + marble.speed * dt;
      if (next >= total) lap++;
      marble.s = next % total;

      // Bridge holes: on a taking lap, crossing the hole s starts the drop.
      // Spur drains (guaranteed) always take; inactive holes (not on this
      // variant) never do.
      for (const [i, f] of falls[lap % variants.length].entries()) {
        if (!f.active) continue;
        const crossed = prevS < f.holeS && next >= f.holeS;
        if (crossed && (f.guaranteed || takesFall(lap, i))) {
          active().getPointAt(f.holeS / total, point);
          falling = {
            t: 0,
            from: point.clone(),
            toS: f.landS,
            drop: f.dropY,
          };
          obj.position.copy(point);
          return;
        }
      }

      active().getPointAt(marble.s / total, point);
      obj.position.copy(point);
      if (sway !== 0) {
        lateral.copy(up).cross(tangent);
        if (lateral.lengthSq() > 1e-8) {
          obj.position.addScaledVector(lateral.normalize(), sway);
        }
      }

      // Roll: spin about the horizontal axis perpendicular to travel.
      axis.copy(up).cross(tangent);
      if (axis.lengthSq() > 1e-8) {
        axis.normalize();
        spin.setFromAxisAngle(axis, (marble.speed * dt) / radius);
        pivot.quaternion.premultiply(spin);
      }
    },
  };

  // Seed the initial pose so frame 0 renders the marble on the track.
  curve.getPointAt(0, point);
  obj.position.copy(point);

  return marble;
}
